import React, { useEffect, useRef, useCallback, createContext, useContext, useState } from 'react';
import styled from 'styled-components';
import { useAppSelector, useAppDispatch } from '../../../store';
import { DataElementContext } from '../../../page-components/common/DataElementContext';
import { processComponentProps } from '../../../page-components/utils/processComponentProps';
import { 
    selectCurrentNotification, 
    selectIsNotificationDisplaying, 
    selectNotificationQueueLength,
    selectNotificationQueue,
    hideCurrentNotification, 
    calculateNotificationProgress,
    restartNotificationTimer,
    showNextNotification,
} from '../../../store/slices/notifications';
import { showPopup as showPopupAction } from '../../../store/slices/templatesConfig';
import { executeAction as executeNotificationAction, getActionLabel, getClickAction, getVisibleActions, NotificationAction } from './utils/notificationActionHelper';

interface NotificationContextValue {
    // Generic notification state
    showNotification: boolean;
    showNotificationPopup: boolean; // manager active for either toast or modal
    currentNotification: any;
    nextNotification: any;
    progress: number; // 0..100 expected by UI
    notificationQueue: any[];
    queueLength: number;
    isDisplaying?: boolean;

    isModalItem?: boolean;
    currentModalPopupId?: string | null;
    isModalOpen?: boolean;

    // Current notification data
    notificationId?: string | number;
    notificationTitle?: string;
    notificationMessage?: string;
    notificationType?: string;
    notificationDuration?: number;
    notificationPriority?: string;
    notificationPersistent?: boolean;
    notificationActions?: NotificationAction[];
    notificationIcon?: string;

    // Next notification preview data
    nextNotificationId?: string | number;
    nextNotificationTitle?: string;
    nextNotificationMessage?: string;
    nextNotificationType?: string;
    nextNotificationDuration?: number;
    nextNotificationPriority?: string;
    nextNotificationPersistent?: boolean;
    nextNotificationActions?: NotificationAction[];

    // Generic actions
    closeNotification: () => void;
    restartNotificationTimer: () => void;
    handleNotificationClick: () => void;
    executeAction: (action: NotificationAction) => Promise<void>;
    getActionLabel: (action: NotificationAction) => string;
    getVisibleActions: () => NotificationAction[];
    getPrimaryActions: () => NotificationAction[];
    getSecondaryActions: () => NotificationAction[];
}

// Create context
const NotificationContext = createContext<NotificationContextValue | null>(null);

// Hooks
export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within the NotificationManager');
    }
    return context;
};

interface NotificationManagerProps {
    children: any;
    styleText: string;
    className: string;
    properties?: any;
}

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const NotificationManager = (componentProps: NotificationManagerProps) => {
    let props = componentProps;

    const dataElementContext = React.useContext(DataElementContext);
    [props] = processComponentProps(props, dataElementContext);

    const dispatch = useAppDispatch();
    const currentNotification = useAppSelector(selectCurrentNotification);
    const isDisplaying = useAppSelector(selectIsNotificationDisplaying);
    const queueLength = useAppSelector(selectNotificationQueueLength);
    const notificationQueue = useAppSelector(selectNotificationQueue);
    const popupVisibility = useAppSelector(state => state.templatesConfig.popupVisibility);

    const [uiProgress, setUiProgress] = useState(0);

    const displayModalAction = currentNotification?.actions?.find(
        (a: NotificationAction) => a?.handler?.type === 'callFrontendMethod' && a?.handler?.method === 'displayModal'
    ) as NotificationAction | undefined;

    // Determine modal strictly by type === 'modal' (keep WS classification consistent)
    const isModalItem = React.useMemo(() => {
        return !!currentNotification && (currentNotification as any).type === 'modal';
    }, [currentNotification]);

    const modalPopupId = React.useMemo(() => {
        if (!currentNotification) return null;
        if (!isModalItem) return null;
        const fromAction = (displayModalAction as any)?.handler?.params?.[0];
        return fromAction || (currentNotification as any)?.modalPopupId || (currentNotification as any)?.identifier || null;
    }, [currentNotification, displayModalAction, isModalItem]);

    const isModalOpen = modalPopupId ? !!popupVisibility[modalPopupId] : false;

    // Track last opened modal to avoid re-opening the same one multiple times
    const lastOpenedModalIdRef = useRef<string | number | null>(null);

    const closeNotification = useCallback(() => {
        dispatch(hideCurrentNotification());
    }, [dispatch]);

    const restartNotificationTimerCb = useCallback(() => {
        dispatch(restartNotificationTimer());
    }, [dispatch]);

    useEffect(() => {
        if (currentNotification && isModalItem && modalPopupId) {
            const alreadyOpenedForThisId = lastOpenedModalIdRef.current === currentNotification.id;
            if (!popupVisibility[modalPopupId] && !alreadyOpenedForThisId) {
                dispatch(showPopupAction({ popupId: modalPopupId }));
                lastOpenedModalIdRef.current = currentNotification.id as any;
            }
        }
        if (!currentNotification || !isModalItem) {
            lastOpenedModalIdRef.current = null;
        }
    }, [currentNotification, isModalItem, modalPopupId, popupVisibility, dispatch]);

    const modalWasOpenRef = useRef(false);
    useEffect(() => {
        if (!isModalItem || !modalPopupId) return;
        const open = !!popupVisibility[modalPopupId];
        if (open) {
            modalWasOpenRef.current = true;
        } else if (modalWasOpenRef.current) {
            modalWasOpenRef.current = false;
            dispatch(hideCurrentNotification());
        }
    }, [popupVisibility, isModalItem, modalPopupId, dispatch]);

    const handleNotificationClick = useCallback(async () => {
        if (!currentNotification?.actions) return;
        const clickAction = getClickAction(currentNotification.actions);
        if (clickAction) {
            await executeNotificationAction(clickAction, () => dispatch(hideCurrentNotification()), { dispatch, currentNotification });
        }
    }, [currentNotification, dispatch]);

    const executeAction = useCallback(async (action: NotificationAction) => {
        return await executeNotificationAction(action, () => dispatch(hideCurrentNotification()), { dispatch, currentNotification });
    }, [dispatch, currentNotification]);

    const getActionLabelHelper = useCallback((action: NotificationAction) => {
        return getActionLabel(action);
    }, []);

    const getVisibleActionsHelper = useCallback(() => {
        return currentNotification?.actions ? getVisibleActions(currentNotification.actions) : [];
    }, [currentNotification]);

    const getPrimaryActions = useCallback(() => {
        const visibleActions = getVisibleActionsHelper();
        return visibleActions.filter(action => action.type === 'primary');
    }, [getVisibleActionsHelper]);

    const getSecondaryActions = useCallback(() => {
        const visibleActions = getVisibleActionsHelper();
        return visibleActions.filter(action => action.type === 'secondary');
    }, [getVisibleActionsHelper]);

    useEffect(() => {
        if (!currentNotification && (notificationQueue?.length || 0) > 0) {
            dispatch(showNextNotification());
        }
    }, [currentNotification, notificationQueue, dispatch]);

    useEffect(() => {
        if (isDisplaying && currentNotification) {
            const progressInterval = setInterval(() => {
                dispatch(calculateNotificationProgress());
            }, 50);
            
            return () => clearInterval(progressInterval);
        }
    }, [isDisplaying, currentNotification, dispatch]);

    // Drive a local UI progress so templates binding width: `${progress}%` always animate
    useEffect(() => {
        let rafId: number | null = null;
        if (isDisplaying && currentNotification) {
            const start = currentNotification.startTime || Date.now();
            const duration = currentNotification.duration || 5000;
            const persistent = !!currentNotification.persistent;

            const tick = () => {
                const elapsed = Date.now() - start;
                const pct = Math.min(100, (elapsed / duration) * 100);
                setUiProgress(pct);
                if (pct < 100 || persistent) {
                    rafId = requestAnimationFrame(tick);
                }
            };
            setUiProgress(0);
            rafId = requestAnimationFrame(tick);
        } else {
            setUiProgress(0);
        }
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [isDisplaying, currentNotification]);

    const contextValue = React.useMemo(() => {
        const nextNotification = notificationQueue.length > 0 ? notificationQueue[0] : null;
        const progressRaw = currentNotification?.progress ?? 0;
        const progressCombined = Math.max(progressRaw, uiProgress);
        const hasToastContent = !!(currentNotification?.title || currentNotification?.message);
        const safeShowToast = isDisplaying && !!currentNotification && !isModalItem && hasToastContent;

        // For modal items, do not expose title/message via context to prevent any toast renderers
        const exposedTitle = isModalItem ? undefined : currentNotification?.title;
        const exposedMessage = isModalItem ? undefined : currentNotification?.message;
        const currentForContext = currentNotification
            ? (isModalItem
                ? { ...currentNotification, title: undefined, message: undefined }
                : currentNotification)
            : null;

        return {
            ...dataElementContext,
            showNotification: safeShowToast,
            showNotificationPopup: safeShowToast,
            currentNotification: currentForContext,
            nextNotification,
            progress: progressCombined,
            notificationQueue,
            queueLength,
            isDisplaying: safeShowToast,
            isModalItem,
            currentModalPopupId: modalPopupId,
            isModalOpen,
            notificationId: currentNotification?.id,
            notificationTitle: exposedTitle,
            notificationMessage: exposedMessage,
            notificationType: currentNotification?.type,
            notificationDuration: currentNotification?.duration,
            notificationPriority: currentNotification?.priority,
            notificationPersistent: currentNotification?.persistent,
            notificationActions: currentNotification?.actions || [],
            notificationIcon: currentNotification?.icon,
            nextNotificationId: nextNotification?.id,
            nextNotificationTitle: nextNotification?.title,
            nextNotificationMessage: nextNotification?.message,
            nextNotificationType: nextNotification?.type,
            nextNotificationDuration: nextNotification?.duration,
            nextNotificationPriority: nextNotification?.priority,
            nextNotificationPersistent: nextNotification?.persistent,
            nextNotificationActions: nextNotification?.actions || [],
            closeNotification,
            restartNotificationTimer: restartNotificationTimerCb,
            handleNotificationClick,
            executeAction,
            getActionLabel: getActionLabelHelper,
            getVisibleActions: getVisibleActionsHelper,
            getPrimaryActions,
            getSecondaryActions,
        } as NotificationContextValue;
    }, [
        dataElementContext,
        isDisplaying,
        currentNotification,
        notificationQueue,
        queueLength,
        isModalItem,
        modalPopupId,
        isModalOpen,
        uiProgress,
        closeNotification,
        restartNotificationTimerCb,
        handleNotificationClick,
        executeAction,
        getActionLabelHelper,
        getVisibleActionsHelper,
        getPrimaryActions,
        getSecondaryActions,
    ]);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default NotificationManager;
